import React from 'react';
import {
  Users,
  Settings,
  LogOut,
  Briefcase,
  Home,
  Headphones,
  Building2,
  GraduationCap
} from 'lucide-react';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { useSidebar } from '../../contexts/SidebarContext';
import { usePermissions } from '../../hooks/usePermissions';

interface MenuItem {
  id: string;
  icon: React.FC<{ className?: string }>;
  label: string;
}

interface SidebarProps {
  currentPage: string;
  onPageChange: (page: string) => void;
  onLogout: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentPage,
  onPageChange,
  onLogout
}) => {
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const { isSuperAdmin } = usePermissions();

  // Menu items for Super Admin
  const superAdminMenuItems: MenuItem[] = [
    { id: 'dashboard', icon: Home, label: 'Tableau de bord' },
    { id: 'organizations', icon: Building2, label: 'Organisations' },
    { id: 'learners', icon: Users, label: 'Apprenants' },
    { id: 'programs', icon: GraduationCap, label: 'Formations' },
    { id: 'projects', icon: Briefcase, label: 'Projets' },
    { id: 'support', icon: Headphones, label: 'Support' },
    { id: 'settings', icon: Settings, label: 'Paramètres' },
  ];

  // Menu items for Client
  const clientMenuItems: MenuItem[] = [
    { id: 'dashboard', icon: Home, label: 'Tableau de bord' },
    { id: 'learners', icon: Users, label: 'Mes apprenants' },
    { id: 'programs', icon: GraduationCap, label: 'Formations' },
    { id: 'projects', icon: Briefcase, label: 'Projets' },
    { id: 'support', icon: Headphones, label: 'Support' },
  ];

  // Select menu based on role
  const menuItems = isSuperAdmin ? superAdminMenuItems : clientMenuItems;

  return (
    <aside
      data-collapsed={isCollapsed}
      className={`fixed left-0 top-0 h-full bg-primary text-white transition-all duration-300 ease-in-out z-50 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
      onMouseEnter={() => setIsCollapsed(false)}
      onMouseLeave={() => setIsCollapsed(true)}
    >
      <div className="h-full flex flex-col p-4">
        <div className="mb-8">
          <Logo />
        </div>

        <div className="flex-1 space-y-2">
          {menuItems.map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => onPageChange(id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors duration-200 ${
                currentPage === id
                  ? 'bg-white/10 text-white'
                  : 'text-white/80 hover:bg-white/5'
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span
                className={`whitespace-nowrap transition-opacity duration-300 ${
                  isCollapsed ? 'opacity-0' : 'opacity-100'
                }`}
              >
                {label}
              </span>
            </button>
          ))}
        </div>

        {/* User info section */}
        <div className="mb-4 px-3">
          <div className={`text-xs text-white/60 transition-opacity duration-300 ${
            isCollapsed ? 'opacity-0' : 'opacity-100'
          }`}>
            {isSuperAdmin ? 'Super Admin' : 'Client'}
          </div>
        </div>

        <div className="space-y-2 border-t border-white/10 pt-4">
          <ThemeToggle />
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors duration-200 text-white/80 hover:bg-white/5"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span
              className={`whitespace-nowrap transition-opacity duration-300 ${
                isCollapsed ? 'opacity-0' : 'opacity-100'
              }`}
            >
              Déconnexion
            </span>
          </button>
        </div>
      </div>
    </aside>
  );
};
